import { useActionParamStore } from '@/lib/action-param-store';
import { TRANSLATE_TARGETS } from '@/lib/command-meta';
import { runChatAction } from '@/lib/run-action';
import { useThreadRuntime } from '@assistant-ui/react';
import type { TranslateTarget } from '@iq/contracts/v1/chat';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@iq/ui';
import { type KeyboardEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Translate-target picker. Renders as a small Radix dialog atop the chat
 * surface, mobile-responsive. Shown when `/translate` is picked in the
 * composer slash palette. Only translate routes through this dialog, since
 * the target language can't be silently defaulted (the user's UI locale
 * isn't necessarily what they want to translate INTO).
 *
 * On choice, dispatches via `runChatAction` which:
 *  - serializes the directive as `:translate[Translate]{name=<target>}` text
 *    so the persisted user bubble renders a Languages-iconed chip,
 *  - sets `metadata.custom.directive` for the backend to read, and
 *  - clears the composer so the slash-palette chip doesn't linger across
 *    the send.
 */
export function ActionParamPopover() {
  const { t } = useTranslation();
  const openFor = useActionParamStore((s) => s.openFor);
  const close = useActionParamStore((s) => s.close);
  const threadRuntime = useThreadRuntime();

  const onPickTranslate = (targetLanguage: TranslateTarget) => {
    runChatAction(threadRuntime, { kind: 'translate', targetLanguage });
    close();
  };

  // Roving-tabindex refs for the language buttons. Index-aligned with
  // `TRANSLATE_TARGETS`; sparse holes are tolerated (filtered out in the
  // key handler).
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  // Auto-focus the first language option whenever the dialog opens so the
  // user can immediately ArrowDown / Enter. Deferring through
  // `requestAnimationFrame` runs the focus after Radix Dialog's FocusScope
  // settles, so this explicit choice wins.
  useEffect(() => {
    if (openFor !== 'translate') return;
    const id = requestAnimationFrame(() => {
      optionRefs.current[0]?.focus();
    });
    return () => cancelAnimationFrame(id);
  }, [openFor]);

  // Keyboard navigation across the language list. Enter falls through to
  // the focused Button's onClick (browser default). Space does the same.
  // ArrowDown / ArrowUp cycle; Home / End jump to ends. Escape is handled
  // by Radix Dialog's own dismiss path.
  const onListKeyDown = (e: KeyboardEvent<HTMLUListElement>) => {
    const buttons = optionRefs.current.filter((b): b is HTMLButtonElement => b !== null);
    if (buttons.length === 0) return;
    const activeIdx = buttons.findIndex((b) => b === document.activeElement);
    let next: number | null = null;
    if (e.key === 'ArrowDown') {
      next = activeIdx < 0 ? 0 : (activeIdx + 1) % buttons.length;
    } else if (e.key === 'ArrowUp') {
      next = activeIdx <= 0 ? buttons.length - 1 : activeIdx - 1;
    } else if (e.key === 'Home') {
      next = 0;
    } else if (e.key === 'End') {
      next = buttons.length - 1;
    }
    if (next !== null) {
      e.preventDefault();
      buttons[next]?.focus();
    }
  };

  return (
    <Dialog open={openFor !== null} onOpenChange={(o) => (o ? null : close())}>
      <DialogContent
        className="w-[calc(100vw-1rem)] max-w-md rounded-2xl gap-3 p-0 sm:p-0"
        data-testid="action-param-popover"
      >
        {openFor === 'translate' && (
          <>
            <DialogHeader className="px-6 pt-6">
              <DialogTitle>{t('chat.commands.translate.label')}</DialogTitle>
              <DialogDescription>{t('chat.commands.translate.description')}</DialogDescription>
            </DialogHeader>
            <ul
              className="flex flex-col gap-2 px-6 pb-6"
              data-testid="action-param-translate-options"
              onKeyDown={onListKeyDown}
            >
              {TRANSLATE_TARGETS.map((opt, idx) => (
                <li key={opt.id}>
                  <Button
                    ref={(el) => {
                      optionRefs.current[idx] = el;
                    }}
                    type="button"
                    variant="outline"
                    className="h-auto w-full justify-start px-4 py-3 text-left"
                    onClick={() => onPickTranslate(opt.id)}
                    data-testid={`action-param-translate-${opt.id}`}
                  >
                    <span className="font-medium">{t(opt.labelKey)}</span>
                  </Button>
                </li>
              ))}
            </ul>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
